import type { ColDef, DataTypeDefinition } from 'ag-grid-community';
import { formatTime, TIME_FORMAT_NUMERICAL_LONG, TIME_FORMAT_NUMERICAL_SHORT } from '@/datetime/dateTime.utilities';
import type { AnyProperty } from '@/utilities.types';
import { sqWorksheetStore } from '@/core/core.stores';
import type { AgGridAsyncProps } from '@/core/tableUtilities/tableUtilities.types';
import { AgGridModules } from '@/core/tableUtilities/tableUtilities.types';
import { isSystemTest } from '@/core/utilities';
import type { MultiRowSelectionOptions } from 'ag-grid-community/dist/types/src/entities/gridOptions';

export const COMMON_AG_GRID_MODULES = [
  AgGridModules.SetFilterModule,
  AgGridModules.ColumnMenuModule,
  AgGridModules.ContextMenuModule,
  AgGridModules.ColumnsToolPanelModule,
  AgGridModules.RowAutoHeightModule,
  AgGridModules.RowStyleModule,
  AgGridModules.PaginationModule,
  AgGridModules.RowDragModule,
  AgGridModules.RowNumbersModule,
  AgGridModules.PinnedRowModule,
  AgGridModules.RowSelectionModule,
  AgGridModules.RowApiModule,
  AgGridModules.TextFilterModule,
  AgGridModules.DateFilterModule,
  AgGridModules.NumberFilterModule,
  AgGridModules.MultiFilterModule,
  AgGridModules.LocaleModule,
  AgGridModules.TooltipModule,
  AgGridModules.ColumnAutoSizeModule,
];

export const DEFAULT_ROW_SELECTION: MultiRowSelectionOptions = {
  mode: 'multiRow',
  enableClickSelection: true,
  checkboxes: true,
  selectAll: 'filtered',
};

export const DEFAULT_COL_DEF: ColDef = {
  filter: 'agMultiColumnFilter',
  filterParams: {
    filters: [
      {
        filter: 'agTextColumnFilter',
      },
      {
        filter: 'agSetColumnFilter',
      },
    ],
  },
  floatingFilter: true,
};

export const STATIC_COL_DEF: ColDef = {
  headerName: '',
  suppressHeaderMenuButton: true,
  suppressMovable: true,
  suppressAutoSize: true,
  suppressFiltersToolPanel: true,
  suppressColumnsToolPanel: true,
  sortable: false,
  resizable: false,
  width: 50,
  floatingFilter: false,
  filter: false,
};

export const CUSTOM_TYPE_DEFINITIONS: AnyProperty<DataTypeDefinition> = {
  dateTime: {
    baseDataType: 'date',
    extendsDataType: 'date',
    valueFormatter: ({ value }) =>
      value ? formatTime(value, sqWorksheetStore.timezone, TIME_FORMAT_NUMERICAL_SHORT) : '-',
  },
  dateTimeWithSeconds: {
    baseDataType: 'date',
    extendsDataType: 'date',
    valueFormatter: ({ value }) =>
      value ? formatTime(value, sqWorksheetStore.timezone, TIME_FORMAT_NUMERICAL_LONG) : '-',
  },
};

export const DEFAULT_AG_GRID_PROPS: AgGridAsyncProps = {
  modules: COMMON_AG_GRID_MODULES,
  accentedSort: true,
  tooltipShowDelay: 500,
  suppressColumnVirtualisation: isSystemTest() || process.env.NODE_ENV === 'test',
  suppressRowVirtualisation: isSystemTest() || process.env.NODE_ENV === 'test',
  defaultColDef: DEFAULT_COL_DEF,
  dataTypeDefinitions: CUSTOM_TYPE_DEFINITIONS,
  rowSelection: {
    mode: 'multiRow',
    enableClickSelection: true,
  },
};

/**
 * A subset of AG-Grid properties to include when you want to print the grid in a nicely formatted way
 */
export const PRINTING_AG_GRID_PROPS: Partial<AgGridAsyncProps> = {
  suppressRowVirtualisation: true,
  suppressColumnVirtualisation: true,
  animateRows: false,
  domLayout: 'print',
};

export const EXTRA_TOPIC_PROPS = { getChartToolbarItems: () => [] };

export const EXTRA_NON_TOPIC_PROPS = {};

export const DEFAULT_PAGINATION_SIZE_SELECTOR = [10, 15, 20, 25, 30, 50, 100];

export const MULTILINE_STYLE = { lineHeight: '1.7em' };
